import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActionArea from "@material-ui/core/CardActionArea";
import { CreditCard, CreditCardTwoTone } from "@material-ui/icons";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import { useCurrency } from "../Context/CurrencyContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1.5),
  },
  card: {
    position: "relative",
    textAlign: "center",
    cursor: "pointer",
    minWidth: 0,
    borderRadius: 14,
    border: "1.5px solid #e2e8f0",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
    transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 18px rgba(15, 23, 42, 0.1)",
    },
    "&:active": {
      transform: "scale(0.97)",
    },
    "& .MuiCardActionArea-root": {
      minHeight: 100,
      display: "flex",
      alignItems: "stretch",
    },
    "& .MuiCardContent-root": {
      width: "100%",
      padding: theme.spacing(1.25, 0.75),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "0.82rem",
      fontWeight: 700,
      color: "#334155",
    },
    [theme.breakpoints.down("xs")]: {
      "& .MuiCardActionArea-root": {
        minHeight: 88,
      },
    },
  },
  iconBadge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    transition: "all 0.18s ease",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
    [theme.breakpoints.down("xs")]: {
      width: 38,
      height: 38,
      marginBottom: 6,
    },
  },
  icon: {
    fontSize: "22px !important",
    lineHeight: "22px !important",
    height: "22px !important",
    width: "22px !important",
  },
  label: {
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
    transition: "color 0.18s ease",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.74rem",
    },
  },
  checkBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    fontSize: "16px !important",
    transition: "opacity 0.18s ease, transform 0.18s ease",
  },
}));

const PaymentOptions = ({ invoiceData, handleInputChange }) => {
  const classes = useStyles();
  const { currency } = useCurrency();

  const handleCardClick = (value) => {
    handleInputChange({ target: { name: "paymentMode", value } });
  };

  const modes = [
    {
      key: "Cash",
      label: "Cash",
      icon: (
        <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>
          {currency}
        </span>
      ),
      accent: "#059669",
      bg: "#ecfdf5",
      selectedBg: "#f0fdf4",
      selectedBorder: "#10b981",
    },
    {
      key: "Card",
      label: "Card",
      icon: <CreditCard className={classes.icon} />,
      accent: "#2563eb",
      bg: "#eff6ff",
      selectedBg: "#f0f7ff",
      selectedBorder: "#3b82f6",
    },
    {
      key: "Online",
      label: "Online",
      icon: (
        <svg
          width="20"
          height="20"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M2 2h2v2H2z" />
          <path d="M6 0v6H0V0zM5 1H1v4h4zM4 12H2v2h2z" />
          <path d="M6 10v6H0v-6zm-5 1v4h4v-4zm11-9h2v2h-2z" />
          <path d="M10 0v6h6V0zm5 1v4h-4V1zM8 1V0h1v2H8v2H7V1zm0 5V4h1v2zM6 8V7h1V6h1v2h1V7h5v1h-4v1H7V8zm0 0v1H2V8H1v1H0V7h3v1zm10 1h-1V7h1zm-1 0h-1v2h2v-1h-1zm-4 0h2v1h-1v1h-1zm2 3v-1h-1v1h-1v1H9v1h3v-2zm0 0h3v1h-2v1h-1zm-4-1v1h1v-2H7v1z" />
          <path d="M7 12h1v3h4v1H7zm9 2v2h-3v-1h2v-1z" />
        </svg>
      ),
      accent: "#7c3aed",
      bg: "#f5f3ff",
      selectedBg: "#faf5ff",
      selectedBorder: "#8b5cf6",
    },
    {
      key: "Credit",
      label: "Credit",
      icon: <CreditCardTwoTone className={classes.icon} />,
      accent: "#d97706",
      bg: "#fffbeb",
      selectedBg: "#fffdf5",
      selectedBorder: "#f59e0b",
    },
  ];

  return (
    <div className={classes.root}>
      {modes.map((mode) => {
        const selected = invoiceData.paymentMode === mode.key;
        return (
          <Card
            key={mode.key}
            className={classes.card}
            style={{
              borderColor: selected ? mode.selectedBorder : "#e2e8f0",
              backgroundColor: selected ? mode.selectedBg : "#ffffff",
              boxShadow: selected
                ? `0 6px 18px ${mode.selectedBorder}33`
                : "0 2px 8px rgba(15, 23, 42, 0.05)",
            }}
            onClick={() => handleCardClick(mode.key)}
            elevation={selected ? 3 : 1}
          >
            {selected && (
              <CheckCircleRoundedIcon
                className={classes.checkBadge}
                style={{ color: mode.accent }}
              />
            )}
            <CardActionArea>
              <CardContent>
                <div
                  className={classes.iconBadge}
                  style={{
                    backgroundColor: selected ? mode.accent : mode.bg,
                    color: selected ? "#ffffff" : mode.accent,
                  }}
                >
                  {mode.icon}
                </div>
                <span
                  className={classes.label}
                  style={{
                    color: selected ? mode.accent : "#334155",
                    fontWeight: selected ? 800 : 700,
                  }}
                >
                  {mode.label}
                </span>
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </div>
  );
};

export default PaymentOptions;
